import { TaskModel } from '@bryntum/gantt';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { DependenciesData } from 'components/atoms/gantt';
import { GanttBarColors } from 'components/colors';
import {
  formatDate,
  GanttType,
  getDueDateForGantt,
} from 'components/organisms/projects/project-plan/timeline/helpers';
import {
  ProjectTemplateData,
  ProjectTemplateProjectPhase,
  ProjectTemplateTask,
  useGetAllProjectPhases,
  useGetAllTasks,
  useTransformTabularData,
} from 'data/templates/projects';

const getDependenciesData = (
  phases: {
    [key: string]: ProjectTemplateProjectPhase;
  },
  tasks: {
    [key: string]: ProjectTemplateTask;
  },
): DependenciesData[] => {
  const dependenciesData: DependenciesData[] = [];

  Object.values(phases).forEach((object) => {
    (object.dependentProjectPhases || []).forEach((projectPhase) => {
      if (phases[projectPhase.projectPhaseId]) {
        dependenciesData.push({
          id: `${object.projectPhaseId}-${projectPhase.projectPhaseId}`,
          fromEvent: `${GanttType.ProjectPhaseBar}-${projectPhase.projectPhaseId}`,
          toEvent: `${GanttType.ProjectPhaseBar}-${object.projectPhaseId}`,
        });
      }
    });
  });

  Object.values(tasks).forEach((object) => {
    (object.dependentTasks || []).forEach((task) => {
      if (tasks[task.taskId]) {
        dependenciesData.push({
          id: `${object.taskId}-${task.taskId}`,
          fromEvent: `${GanttType.TaskBar}-${task.taskId}`,
          toEvent: `${GanttType.TaskBar}-${object.taskId}`,
        });
      }
    });
  });

  return dependenciesData;
};

const generateGanttColorsOnce = (data: ProjectTemplateData[]) => {
  const colors = {};

  const iterator = (entity) => {
    entity.forEach(({ rootIndex, id, children }) => {
      colors[id] = GanttBarColors[rootIndex % GanttBarColors.length];
      iterator(children);
    });
  };

  iterator(data);

  return colors;
};

const mapAdditionalValues = (
  data: ProjectTemplateData[],
  tasks: { [key: string]: ProjectTemplateTask },
  ganttColors: { [key: string]: string },
  parentLevel: number,
) => {
  // let isMilestone: boolean;
  const currentLevel = parentLevel + 1;

  return data
    .filter((d) => !d.data.isNew)
    .map((item) => {
      // isMilestone = (item as ProjectTemplateTaskData).data.type === 'MILESTONE';
      const {
        type,
        duration,
        startDay,
        children,
        rootIndex,
        key,
        id,
        ...rest
      } = item;

      return {
        ...rest,
        type,
        key,
        id: key,
        _id: id,
        name: rest.title,
        expanded: true,
        manuallyScheduled: true,
        resizable: type !== GanttType.ProjectPhaseBar,
        cls: `gantt-${type.toLowerCase()} ${
          item.private ? 'gantt-private-row' : ''
        } gantt-bg-${ganttColors[id]} gantt-level-${currentLevel}`,
        actualDuration: duration,
        // duration: isMilestone ? 0 : duration,
        // duration,
        rootIndex,
        startDay,
        startDate: formatDate(moment().add(startDay, 'day')),
        endDate: getDueDateForGantt(
          moment().add(startDay + duration - 1, 'day'),
        ),
        children: children
          ? mapAdditionalValues(children, tasks, ganttColors, currentLevel)
          : [],
      };
    });
};

export const useTemplateGanttDataConfig = (
  phases?: ProjectTemplateProjectPhase[] | null,
) => {
  const [initialised, setInitialised] = useState(false);
  const [eventsData, setEventsData] = useState<any[]>([]);
  const [dependenciesData, setDependenciesData] = useState<any[]>([]);

  const project = useTransformTabularData(phases);
  const tasks = useGetAllTasks();
  const projectPhases = useGetAllProjectPhases();

  useEffect(() => {
    if (!initialised && !isEmpty(project) && !isEmpty(tasks)) {
      setInitialised(true);
      setEventsData(
        mapAdditionalValues(
          project,
          tasks,
          generateGanttColorsOnce(project),
          -1,
        ),
      );
      setDependenciesData(getDependenciesData(projectPhases, tasks));
    }
  }, [initialised, project, projectPhases, tasks]);

  /**
   * Handles dynamic template preview
   * where phases will be returned from API response
   * for every single preview
   */
  useEffect(() => {
    if (phases === undefined) {
      return;
    }
    setEventsData(
      mapAdditionalValues(project, tasks, generateGanttColorsOnce(project), -1),
    );

    setDependenciesData(getDependenciesData(projectPhases, tasks));
  }, [phases, project, projectPhases, tasks]);

  return useMemo(
    () => ({
      project: {
        taskModelClass: TaskModel,
        eventsData,
        dependenciesData,
      },
    }),
    [dependenciesData, eventsData],
  );
};
